/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAncestorPath } from '@squiz/dxp-console-support';
import { Breadcrumbs, BreadcrumbsProps } from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import { LoadingScreenEmpty } from 'apps/console-core/src/Screens/LoadingScreenEmpty/LoadingScreenEmpty';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { useEventSchedule, useEventSubscriptionRule } from '../../../../Services/event-bus';
import { EventSubscriptionBuilderForm } from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm';
import { EventSubscriptionBuilderFormProps } from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm.props';

/**
 * The event subscription builder dialog launcher.
 */
export const EventSubscriptionBuilderEdit = (): JSX.Element => {
  const { t } = useTranslation();

  const params = useParams();
  const subscriptionId = params.subscriptionId as string;

  const [eventSubscriptionRule] = useEventSubscriptionRule(subscriptionId);
  const [eventSchedule, eventScheduleError] = useEventSchedule(subscriptionId);

  // Labels
  const labels = useMemo(() => {
    return {
      eventService: t(CONFIGURATION_STRINGS.eventService),
      eventSubscriptions: t(CONFIGURATION_STRINGS.eventSubscriptions),
      heading: t(CONFIGURATION_STRINGS.createEventSubscription),
    };
  }, [t]);

  // Breadcrumbs
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const eventServicePath = useAncestorPath();
  const eventSubscriptionPath = useAncestorPath(false, 2);

  const breadcrumbProps: BreadcrumbsProps = {
    currentPageName: labels.heading,
    links: [
      ...capabilityBreadcrumbs,
      {
        name: labels.eventService,
        url: eventServicePath,
      },
      {
        name: labels.eventSubscriptions,
        url: eventSubscriptionPath,
      },
    ],
  };

  const formProps = {} as EventSubscriptionBuilderFormProps;
  let screenHeading = labels.heading;

  if (!eventSubscriptionRule) {
    return <LoadingScreenEmpty />;
  }

  if (!eventSchedule && !eventScheduleError) {
    return <LoadingScreenEmpty />;
  }

  // Pass in props to set up the form for editing an event subscription
  formProps.subscriptionId = subscriptionId;
  formProps.eventSubscription = eventSubscriptionRule;
  formProps.eventSchedule = eventSchedule;

  // Render the heading and breadcrumbs with the event subscription's name
  const { name } = eventSubscriptionRule;
  screenHeading = name;
  breadcrumbProps.currentPageName = name;

  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="event-subscriptions-breadcrumbs" />;
  const screenContent = (
    <>
      <EventSubscriptionBuilderForm {...formProps} />
    </>
  );

  return (
    <DxpScreen breadcrumbs={breadcrumbs} heading={screenHeading} showSeparator>
      {screenContent}
    </DxpScreen>
  );
};
